import React, { useRef } from 'react';
import { cn } from '../../utils/cn';

export interface TabItem {
  key: string;
  label: string;
  /** Small count pill rendered after the label, e.g. an open-item count. */
  badge?: React.ReactNode;
}

export interface TabsProps {
  tabs: TabItem[];
  /** Key of the currently active tab. */
  value: string;
  onChange: (key: string) => void;
  /**
   * `primary` = 2px brand-gradient underline (top-level navigation).
   * `sub` = lighter 2px solid underline (nested/sub navigation).
   */
  variant?: 'primary' | 'sub';
  /** Accessible label for the tablist when the surrounding context doesn't already describe it. */
  'aria-label'?: string;
  className?: string;
}

const focusRing =
  'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(128,110,225,.3)]';

/**
 * Controlled horizontal tabs primitive. Supports a `primary` (brand-gradient underline)
 * and `sub` (lighter solid underline) variant, roving keyboard navigation
 * (ArrowLeft/ArrowRight/Home/End), and an optional badge count per tab.
 *
 * This is a new primitive introduced in the Cycle 1 design-system pass — existing tab
 * bars (Details subtabs, SOW/EDF, PortfolioDetail, SectionNavigation) are not migrated
 * to it yet.
 */
const Tabs: React.FC<TabsProps> = ({
  tabs,
  value,
  onChange,
  variant = 'primary',
  'aria-label': ariaLabel,
  className,
}) => {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const selectAndFocus = (key: string) => {
    onChange(key);
    tabRefs.current[key]?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (tabs.length === 0) return;

    let nextIndex: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    selectAndFocus(tabs[nextIndex].key);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex items-center gap-1 overflow-x-auto', className)}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.key === value;
        const isPrimary = variant === 'primary';

        return (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[tab.key] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            style={
              isActive && isPrimary
                ? { borderImage: 'var(--brand-gradient) 1', borderImageSlice: 1 }
                : undefined
            }
            className={cn(
              'inline-flex items-center gap-2 whitespace-nowrap bg-transparent cursor-pointer',
              'border-b-2 border-transparent transition-colors duration-150',
              focusRing,
              isPrimary ? 'px-4 py-3 text-sm font-medium' : 'px-3 py-2 text-sm font-medium',
              isActive ? 'text-[var(--foreground)]' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]',
              isActive && !isPrimary && 'border-[var(--primary-500)]'
            )}
          >
            {tab.label}
            {tab.badge !== undefined && (
              <span className="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-[var(--muted)] px-1.5 text-[10px] font-semibold text-[var(--muted-foreground)]">
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
